import { useEffect, useId, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Button,
  Callout,
  CalloutTitle,
  Stack,
} from '@pypestream/design-system';
import { useTranslation } from '@pypestream/translations';

import { useCollabSessionContext } from './collab-session-context';
import type { CollabSession } from './use-collab-session';

const MESSAGE_KEY = {
  connecting: 'pro-studio/workflows:live_editing.connecting',
  offline: 'pro-studio/workflows:live_editing.offline',
  error: 'pro-studio/workflows:live_editing.error',
  connected: 'pro-studio/workflows:live_editing.connected',
  read_only: 'pro-studio/workflows:live_editing.read_only',
  import_lock: 'pro-studio/workflows:live_editing.import_lock',
  editable_again: 'pro-studio/workflows:live_editing.editable_again',
  import_done: 'pro-studio/workflows:live_editing.import_done',
} as const;

/** The status to tell the user about, or `null` when there is nothing to say. */
const statusToShow = (session: CollabSession | null) => {
  if (!session) return null;
  if (session.failed) return 'error';
  if (session.pending) return 'connecting';
  const { store } = session;
  const status = store?.connectionStatus;
  if (status && status !== 'synced') return status;
  // PSTUDIO-4451: synced, but the document will not take this user's writes
  // — the table has turned view-only, and nothing else on screen says why.
  if (store?.accessStatus === 'read-only') return 'read_only';
  if (store?.editorStatus === 'locked-for-import') return 'import_lock';
  return null;
};

const ACCESS_STATES: readonly string[] = ['read_only', 'import_lock'];

/**
 * How long a freshly mounted status region stays empty before it speaks: long
 * enough for a screen reader to register the region (JAWS and NVDA rebuild
 * their buffer asynchronously), and free, since the region is hidden.
 */
const REGION_READY_DELAY_MS = 500;

/**
 * The live-editing connection status (PSTUDIO-4449), drawn by the Workflows
 * table directly above its footer. Renders nothing while `synced`, so it takes
 * no space then.
 *
 * Not a live region: it lives inside the table, which unmounts every time the
 * page flips to its loader (publish, draft switch, re-entry), and a region
 * inserted together with its text is announced unreliably — the PSTUDIO-4240
 * rule. `CollabConnectionAnnouncer` speaks for it from a region the page keeps
 * mounted.
 */
export const CollabConnectionBanner = observer(() => {
  const [t] = useTranslation();
  const session = useCollabSessionContext();
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  // Focus lands here on reconnect. If the session then recovers, unmounting
  // would drop that focus to <body>, so the banner stays — saying it is back
  // online — until focus moves on.
  const [holdsFocus, setHoldsFocus] = useState(false);
  const live = statusToShow(session);
  const status = live ?? (session && holdsFocus ? 'connected' : null);

  if (!session || !status) return null;

  const reconnect = () => {
    // The button disappears as the new session starts connecting; focus would
    // otherwise drop to <body>. The banner itself stays, saying "Connecting…".
    ref.current?.focus();
    session.reload();
  };

  return (
    <div
      ref={ref}
      tabIndex={-1}
      role="group"
      aria-labelledby={titleId}
      onFocus={() => setHoldsFocus(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          setHoldsFocus(false);
        }
      }}
      className="c-live-editing-status"
      // eslint-disable-next-line react/no-unknown-property -- the repo's e2e id attribute
      test-id="ps-live-editing-banner"
      data-status={status}
    >
      <Callout size="small" theme="light">
        <Stack nowrap alignItems="center" justifyContent="space-between">
          <CalloutTitle id={titleId}>{t(MESSAGE_KEY[status])}</CalloutTitle>
          {status === 'error' && (
            <Button
              size="small"
              variant="secondary"
              test-id="ps-live-editing-reconnect"
              onClick={reconnect}
            >
              {t('pro-studio/workflows:live_editing.reconnect')}
            </Button>
          )}
        </Stack>
      </Callout>
    </div>
  );
});

/**
 * Screen-reader voice of the banner above. Mount it where it survives the
 * page's loading flips (next to the page's own `role="status"` region) and it
 * keeps one region mounted while only its text changes.
 */
export const CollabConnectionAnnouncer = observer(() => {
  const [t] = useTranslation();
  const status = statusToShow(useCollabSessionContext());
  // PSTUDIO-4449: the session outlives this page, so the page can open on a
  // problem already present. The region mounts EMPTY and says nothing until it
  // has been in the document for a moment — a region inserted with its text
  // already in it is announced unreliably (PSTUDIO-4241). `useLiveRegionReady`
  // waits only for an effect, which is not "later" here: React flushes the
  // passive effects of a discrete update (the click that opened this page)
  // before paint. Once ready, every change lands at once.
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setReady(true), REGION_READY_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);
  // Recovery is news only after the user was told something went wrong; the
  // first connect of every visit stays silent, and so does the end of a
  // problem that cleared before the region could say it.
  const lastProblem = useRef<string | null>(null);
  const [recovered, setRecovered] = useState<
    'connected' | 'editable_again' | 'import_done' | null
  >(null);

  useEffect(() => {
    if (!ready) return;
    if (
      status === 'offline' ||
      status === 'error' ||
      (status && ACCESS_STATES.includes(status))
    ) {
      lastProblem.current = status;
      setRecovered(null);
    } else if (status === null && lastProblem.current) {
      // PSTUDIO-4451: getting edit access back is its own message; after an
      // import (PSTUDIO-4452) it says the import is over — every coworker's
      // table has just been replaced under them.
      setRecovered(
        lastProblem.current === 'import_lock'
          ? 'import_done'
          : ACCESS_STATES.includes(lastProblem.current)
            ? 'editable_again'
            : 'connected'
      );
      lastProblem.current = null;
    } else if (status !== null) {
      setRecovered(null);
    }
  }, [status, ready]);

  const text = !ready
    ? ''
    : status
      ? t(MESSAGE_KEY[status])
      : recovered
        ? t(MESSAGE_KEY[recovered])
        : '';

  return (
    <div role="status" className="u-visually-hidden">
      {text}
    </div>
  );
});
